<template>
  <div class="mortgage-calculator-component">
    <div class="d-grid grid-xl-cols-2 gap-05 text-dark">
      <!-- Left input side -->
      <div class="p-45 p-xl-5">
        <p class="mb-2 font-size-14">Choose one of Your Favourite Homes or tell us your property price:</p>
        <div class="row mb-45 mb-lg-35">

          <!-- Favourite homes -->
          <div class="col-12 col-lg-6 form-floating mb-3 mb-lg-0">
            <select
              class="form-select mx-auto font-size-14"
              style="height: 50px; min-height: 50px; padding-top: 1.125rem;"
              id="developmentHouseType"
              v-model="selectedDevelopmentHouseType"
              v-bind:disabled="developmentHouseTypes.length === 0"
              @change="updatePropertyPrice"
            >
              <option :value="null">Select</option>
              <option
                v-for="devHouseType in developmentHouseTypes"
                :key="devHouseType.id"
                :value="devHouseType"
              >
                {{ devHouseType.house_type.name }}
              </option>
            </select>
            <label for="developmentHouseType" class="text-light font-weight-light font-size-14">Choose a saved home</label>
          </div>

          <!-- Property price -->
          <div class="d-flex align-items-baseline gap-2 col-12 col-lg-6">
            <p class="mb-0">£</p>
            <div class="form-floating w-100">
              <vue-autonumeric
                v-model="price"
                class="form-control"
                id="price"
                :options="[
                  'British',
                    {
                      minimumValue: '0',
                      integer: true,
                      allowDecimalPadding: false
                    }
                ]"
                @input="calcMonthlyRepayments"
              ></vue-autonumeric>
              <label for="price" class="text-light font-weight-light font-size-14">Property price</label>
            </div>
          </div>
        </div>

        <div class="row mb-45 mb-lg-35">
          <!-- Deposit -->
          <p class="mb-2 font-size-14">What is your deposit:</p>
          <div class="d-flex align-items-center gap-2 col-12 col-lg-6">
            <p class="mb-0">£</p>
            <div class="form-floating">
              <vue-autonumeric
                v-model="deposit"
                :options="[
                  'British',
                    {
                      minimumValue: '0',
                      integer: true,
                      allowDecimalPadding: false
                    }
                ]"
                class="form-control"
                @input="calcMonthlyRepayments"
              ></vue-autonumeric>
              <label for="deposit" class="text-light font-weight-light font-size-14">Your deposit</label>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Interest rate -->
          <div class="col-12 col-md-6">
            <p class="mb-2 font-weight-light font-size-14">What is your interest rate:</p>
            <div class="plus-minus d-flex gap-2 align-items-center justify-content-between">
              <button type="button" class="btn btn-icon bg-cg rounded-circle" @click="decreaseInterestRate" aria-label="decrease the interest rate by 0.1%">
                <i class="far fa-minus fa-3x"></i>
              </button>

              <div class="bg-white rounded-pill w-100 p-3 text-center">
                <h5 class="mb-0 font-size-14 font-weight-normal font-albra-regular" aria-label="interest rate percentage">{{ interestRate }}%</h5>
              </div>

              <button type="button" class="btn btn-icon bg-cg rounded-circle" @click="increaseInterestRate" aria-label="increase the interest rate by 0.1%">
                <i class="far fa-plus fa-3x"></i>
              </button>
            </div>
          </div>

         <!-- Mortgage Term -->
          <div class="col-12 col-md-6">
            <p class="mb-2 font-weight-light font-size-14">What is your mortgage term:</p>
            <div class="plus-minus d-flex gap-2 align-items-center justify-content-between">
              <button type="button" class="btn btn-icon bg-cg rounded-circle" @click="decreaseRepaymentPeriod" aria-label="decrease the mortgage term by 1 year">
                <i class="far fa-minus"></i>
              </button>

              <div class="bg-white rounded-pill w-100 p-3 text-center">
                <h5 class="mb-0 font-size-14 font-weight-normal font-albra-regular" aria-label="mortgage term years">{{ repaymentPeriod }} years</h5>
              </div>

              <button type="button" class="btn btn-icon bg-cg rounded-circle" @click="increaseRepaymentPeriod" aria-label="increase the mortgage term by 1 year">
                <i class="far fa-plus"></i>
              </button>
            </div>
          </div>
        </div>

      </div>

      <!-- Right output side -->
      <div class="p-3 py-xl-45 px-lg-5">
        <div class="row mb-1 mb-lg-3">
          <!-- Deposit percentage -->
          <div class="col-12 bg-white d-flex p-3 p-lg-4 rounded-2">
            <div class="d-flex gap-3">
              <div>
                <div class="d-flex align-items-baseline gap-2 mb-3">
                  <h4 class="h3 mb-0 text-cg font-weight-lighter font-albra-medium">{{ depositPercentage }}%</h4>
                  <p class="mb-0 text-peach font-size-20 font-weight-lighter font-albra-medium">deposit</p>
                </div>
                <div>
                  <p class="mb-0 font-size-14">This means you're asking for a mortgage with a LTV of {{ loanToValue }}%.</p>
                </div>
              </div>
              <div
                ref="progress"
                style="width: 100px; height: 100px;"
                class="d-none d-xl-block"
              ></div>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Monthly repayments -->
          <div class="col-12 bg-dark p-3 p-lg-4 rounded-2 text-white">
            <div class="d-flex align-items-baseline gap-2 mb-3">
              <h4 class="h3 mb-0 text-white font-weight-lighter font-albra-medium">&pound;{{ monthlyRepayments | money }}</h4>
              <p class="mb-0 text-peach font-size-20 font-weight-lighter font-albra-medium">monthly repayments</p>
            </div>
            <div class="d-flex flex-column flex-sm-row gap-4 align-items-end justify-content-sm-between">
              <div>
                <p class="mb-2 font-size-14 font-weight-normal">Need an Agreement in Principle?</p>
                <img src="/img/rsc.png" alt="RSC" width="97" height="73" />
              </div>
              <div>
                <a @click="redirectToMortgageProvider" class="btn btn-primary btn-with-icon gap-5 w-100">
                  Apply Now
                  <i class="fas fa-arrow-right"></i>
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const ProgressBar = require("progressbar.js");

export default {
  data() {
    return {
      deposit: 0,
      price: 100_000,
      monthlyRepayments: 0,
      interestRate: 4.0,
      repaymentPeriod: 25,
      progressCircle: null,
      selectedDevelopmentHouseType: null,
    };
  },

  computed: {
    depositPercentage() {
      const percentage = (this.deposit / this.price) * 100;
      return Math.round(percentage * 100) / 100;
    },

    loanToValue() {
      return Math.round(100 - this.depositPercentage);
    },
  },

  props: {
    developmentHouseType: {
      required: true,
    },

    developmentHouseTypes: {
      type: Array,
      required: false,
    },

    redirectUrl: {
      type: String,
      required: true,
    },
  },

  created() {
    // this.selectedDevelopmentHouseType = this.developmentHouseType;
    if (this.developmentHouseTypes.length > 0) {
      this.selectedDevelopmentHouseType = this.developmentHouseTypes[0];

      // Set the price to the first development house type.
      this.price = this.selectedDevelopmentHouseType.from_price;
    }

    // Set the deposit to 10% of the price.
    this.deposit = Math.floor(this.price * 0.1);

    this.$nextTick(() => {
      this.progressCircle = new ProgressBar.Circle(this.$refs.progress, {
        strokeWidth: 50,
        // fill: "#fff",
        easing: "easeInOut",
        duration: 1400,
        color: "#3d441e",
        trailColor: "#beccc2",
        trailWidth: 50,
        svgStyle: null
      });
    });

    this.calcMonthlyRepayments();
  },

  methods: {
    decreaseInterestRate() {
      this.interestRate = this.interestRate - 0.1;

      if (this.interestRate < 0.1) {
        this.interestRate = 0.1;
      }

      // Ensure 1 decimal place max.
      this.interestRate = Math.round(this.interestRate * 10) / 10;

      this.calcMonthlyRepayments();
    },

    increaseInterestRate() {
      this.interestRate = this.interestRate + 0.1;

      // Ensure 1 decimal place max.
      this.interestRate = Math.round(this.interestRate * 10) / 10;

      this.calcMonthlyRepayments();
    },

    decreaseRepaymentPeriod() {
      this.repaymentPeriod = this.repaymentPeriod - 1;

      if (this.repaymentPeriod < 1) {
        this.repaymentPeriod = 1;
      }

      this.calcMonthlyRepayments();
    },

    increaseRepaymentPeriod() {
      this.repaymentPeriod = this.repaymentPeriod + 1;
      this.calcMonthlyRepayments();
    },

    calcMonthlyRepayments() {
      const amountToRepay = this.price - this.deposit;
      const interest = this.interestRate / 100 / 12;
      const months = this.repaymentPeriod * 12;

      this.monthlyRepayments =
        (amountToRepay * interest * Math.pow(1 + interest, months)) /
        (Math.pow(1 + interest, months) - 1);

      // Ensure 2 decimal places max.
      this.monthlyRepayments = Math.round(this.monthlyRepayments);

      this.$nextTick(() => {
        this.drawProgressCircle();
      });
    },

    updatePropertyPrice() {
      this.price = this.selectedDevelopmentHouseType.from_price;
      this.calcMonthlyRepayments();
    },

    drawProgressCircle() {
      this.progressCircle.animate(this.depositPercentage / 100); // Number from 0.0 to 1.0
    },

    redirectToMortgageProvider() {
      // Example URL
      // https://mortgage.mortgagebureau.net/welcome?redirect_url=https://www.castlegreenhomes.uk/our-developments/north-west/willow-fields-warrington/the-heatherington/&development=The+Heatherington

      let redirectUrl = this.redirectUrl;

      redirectUrl = 'https://rscnewhomes.com/castle-green-homes/';

      // redirectUrl += encodeURIComponent(window.location.href);
      //
      // redirectUrl += '&development=';
      //
      // redirectUrl += encodeURIComponent(this.selectedDevelopmentHouseType.house_type.name || 'Unknown');

      // Open in new tab.
      window.open(redirectUrl, '_blank').focus();
    }
  }
};
</script>
